import React, { Fragment, startTransition, useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import {
  CANVAS_CREATIVE_ID,
  PREDEFINED_MESSAGE,
  RESPONSE_ERROR,
  STREAM_STATE,
  USER_TEXT_TRACKING_LENGTH,
} from 'site-modules/canvas/constants/canvas';
import { ellipsisTruncate } from 'client/utils/string-utils';
import { fireNoStreamIdTracking } from 'site-modules/canvas/utils/canvas-tracking';
import { useResponseStream } from 'site-modules/canvas/hooks/use-response-stream';
import { CanvasLoader } from 'site-modules/canvas/components/canvas-loader/canvas-loader';
import { AppsRenderer } from 'site-modules/canvas/components/chat-messages/ai-message/apps-renderer/apps-renderer';
import { AnimationSchedule } from 'site-modules/canvas/components/animation-schedule/animation-schedule';
import { ThinkingText } from './thinking-text/thinking-text';
import { Markdown } from './markdown/markdown';
import { ActionButtons } from './action-buttons/action-buttons';

import './ai-message.scss';

export function AiMessage({
  isChatMessage,
  message,
  setLoading = noop,
  onLinkClick = noop,
  onMCPLinkClick = noop,
  customMarkdownComponents = null,
  setStreamState = noop,
  onComplete: onCompleteProp = noop,
}) {
  const { text, userText, responsePromise, isCanceled, struct, generatedResponseId, historyResponseId } = message;
  const [isThinking, setThinking] = useState(true);
  const [responseId, setResponseId] = useState(null);
  const [isStreamingCompleted, setStreamingCompleted] = useState(!!text);
  const [isAnimationCompleted, setAnimationCompleted] = useState(!!text);
  const [errorText, setErrorText] = useState(null);
  const [mcpAppsInitialized, setMcpAppsInitialized] = useState(false);
  const isAnimationDisabled = isAnimationCompleted || !!text;

  const setStreamStateRef = useRef(setStreamState);
  const onCompleteRef = useRef(onCompleteProp);
  const setLoadingRef = useRef(setLoading);

  useLayoutEffect(() => {
    setStreamStateRef.current = setStreamState;
    onCompleteRef.current = onCompleteProp;
    setLoadingRef.current = setLoading;
  });

  const messageId = responseId || historyResponseId;
  const trackingValue = `search=${ellipsisTruncate(userText, USER_TEXT_TRACKING_LENGTH)}`;

  const handleError = useCallback(
    error => {
      setThinking(false);
      setLoadingRef.current(false);
      if (userText !== PREDEFINED_MESSAGE.WELCOME) {
        setErrorText(error);
      }
    },
    [userText]
  );

  const onResponseError = useCallback(
    errorTxt => {
      setStreamStateRef.current(STREAM_STATE.NO_STREAM);
      handleError(errorTxt);
    },
    [handleError]
  );

  const completeResponse = useCallback(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: {
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        action_name: TrackingConstant.VIEW_SEARCH_RESULT,
        subaction_name: TrackingConstant.VIEW_AUTO_COMPLETE_RESULT,
        creative_id: CANVAS_CREATIVE_ID,
        value: trackingValue,
        resp_uuid: responseId,
      },
    });

    setStreamStateRef.current(STREAM_STATE.NO_STREAM);
    onCompleteRef.current(message);
    setLoadingRef.current(false);
  }, [message, responseId, trackingValue]);

  const handleStreamComplete = useCallback(responseText => {
    setStreamingCompleted(true);
    if (!responseText) {
      setThinking(false);
    }
  }, []);

  const [responseText, stopResponse, structData] = useResponseStream(responseId, {
    bodyStream: null,
    onComplete: handleStreamComplete,
    onError: onResponseError,
    creativeId: CANVAS_CREATIVE_ID,
  });

  useEffect(() => {
    if (responsePromise) {
      responsePromise
        .then(id => {
          setResponseId(id);
        })
        .catch(error => {
          fireNoStreamIdTracking(error, CANVAS_CREATIVE_ID, generatedResponseId);
          /* istanbul ignore else */
          if (error?.name !== 'AbortError') {
            handleError(RESPONSE_ERROR.SOMETHING_WRONG);
          }
        });
    }
    if (text) {
      startTransition(() => {
        setThinking(false);
        setStreamingCompleted(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsePromise, text]);

  useEffect(() => {
    if (!isCanceled) {
      return;
    }

    stopResponse();

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        action_name: TrackingConstant.VIEW_SEARCH_RESULT,
        subaction_name: TrackingConstant.VIEW_AUTO_COMPLETE_RESULT,
        creative_id: CANVAS_CREATIVE_ID,
        value: trackingValue,
        resp_uuid: responseId,
        selection_type: 'cancel_message',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCanceled]);

  const onRender = useCallback(() => {
    if (!text) {
      setStreamStateRef.current(STREAM_STATE.STREAMING);
    }
    setThinking(false);
  }, [text]);

  const onAnimationComplete = useCallback(() => {
    setAnimationCompleted(true);
  }, []);

  useEffect(() => {
    if (!responsePromise) {
      return;
    }

    if (isStreamingCompleted && isAnimationCompleted) {
      completeResponse();
    }
  }, [completeResponse, isAnimationCompleted, isStreamingCompleted, responsePromise]);

  const handleAppsInitialized = useCallback(() => {
    setMcpAppsInitialized(true);
  }, []);

  return (
    <AnimationSchedule
      isReadyToComplete={isStreamingCompleted && !isThinking}
      onAnimationComplete={onAnimationComplete}
    >
      <div className="ai-message" data-chat-message={isChatMessage ? 'true' : undefined}>
        {isThinking && !isCanceled && (
          <div className="d-flex align-items-center gap-0_75 text-cool-gray-10">
            <CanvasLoader />
            <ThinkingText isIterating={userText !== PREDEFINED_MESSAGE.WELCOME} />
          </div>
        )}
        <div>
          {!!(responseId || text) && (
            <>
              <Markdown
                text={text || responseText}
                onLinkClick={onLinkClick}
                messageId={messageId}
                customComponents={customMarkdownComponents}
                onRender={onRender}
                isAnimationDisabled={isAnimationDisabled}
                isCanceled={isCanceled}
              />
              {!isThinking && (
                <AnimationSchedule.BaseScheduledNode unitId="mcp-apps" isAnimationDisabled={!!text}>
                  {({ visible }) => (
                    <div hidden={!visible}>
                      <AppsRenderer
                        structData={struct || structData}
                        onMCPLinkClick={onMCPLinkClick}
                        messageId={messageId}
                        animate={!!responseId}
                        visible={visible}
                        onAppsInitialized={handleAppsInitialized}
                        isStreamingCompleted={isStreamingCompleted}
                      />
                    </div>
                  )}
                </AnimationSchedule.BaseScheduledNode>
              )}
            </>
          )}
          {!!errorText && (
            <AnimationSchedule.BaseScheduledNode unitId="error-text" isAnimationDisabled={!!text}>
              {({ visible }) => (visible ? <div className="text-cool-gray-10">{errorText}</div> : null)}
            </AnimationSchedule.BaseScheduledNode>
          )}
        </div>
        {isCanceled && <i className="text-cool-gray-50">You stopped this response</i>}
        {userText !== PREDEFINED_MESSAGE.WELCOME && (responseId || text) && !isThinking && (
          <AnimationSchedule.BaseScheduledNode unitId="action-buttons" isAnimationDisabled={!!text}>
            {({ visible }) => {
              if (!mcpAppsInitialized || !visible) {
                return null;
              }

              return <ActionButtons messageText={text || responseText} isCanceled={isCanceled} messageId={messageId} />;
            }}
          </AnimationSchedule.BaseScheduledNode>
        )}
      </div>
    </AnimationSchedule>
  );
}

AiMessage.propTypes = {
  isChatMessage: PropTypes.bool,
  message: PropTypes.shape({
    text: PropTypes.string,
    userText: PropTypes.string,
    responsePromise: PropTypes.instanceOf(Promise),
    isCanceled: PropTypes.bool,
    generatedResponseId: PropTypes.string,
    struct: PropTypes.shape({
      ui_tool_calls: PropTypes.arrayOf(PropTypes.shape({})),
      meta: PropTypes.shape({
        ui: PropTypes.shape({
          component: PropTypes.string,
        }),
      }),
    }),
    historyResponseId: PropTypes.string,
  }).isRequired,
  setLoading: PropTypes.func,
  onLinkClick: PropTypes.func,
  onMCPLinkClick: PropTypes.func,
  customMarkdownComponents: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func])),
  setStreamState: PropTypes.func,
  onComplete: PropTypes.func,
};
